import { useMemo, useState } from 'react';
import AppShell from '../app/AppShell';
import { FeedbackMessage, PaginationFooter, ToastStack } from '../components/common';
import { DashboardEmptyState, SummaryCard } from '../components/dashboard';
import useStaffDashboard from '../hooks/useStaffDashboard';
import { surfaceClassName } from '../utils/dashboardStyles';

const SUPPLIER_STOCK_PAGE_SIZE = 20;

function StaffDashboardPage({ navigationItems, activePath, onNavigate }) {
  const { summaryCards, supplierStocks, isLoading, loadError } = useStaffDashboard();
  const toasts = [
    loadError ? { id: 'staff-dashboard-load-error', tone: 'error', message: loadError } : null,
  ].filter(Boolean);

  return (
    <AppShell
      navigationItems={navigationItems}
      activePath={activePath}
      onNavigate={onNavigate}
    >
      <ToastStack toasts={toasts} />
      <section
        className="mt-5 grid gap-4 px-4 sm:mt-6 sm:px-6 sm:grid-cols-2 lg:px-[26px] xl:grid-cols-3"
        aria-label="Staff summary metrics"
      >
        {isLoading ? (
          <div className="sm:col-span-2 xl:col-span-3">
            <FeedbackMessage message="Loading staff dashboard." />
          </div>
        ) : null}

        {summaryCards.map((card) => (
          <SummaryCard key={card.id} {...card} />
        ))}
      </section>

      <section className="mt-5 px-4 sm:px-6 lg:px-[26px]" aria-label="Supplier seaweed stocks">
        <SupplierStocksCard items={supplierStocks} />
      </section>
    </AppShell>
  );
}

function SupplierStocksCard({ items }) {
  const [currentPage, setCurrentPage] = useState(1);
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / SUPPLIER_STOCK_PAGE_SIZE));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * SUPPLIER_STOCK_PAGE_SIZE;
  const endIndex = Math.min(startIndex + SUPPLIER_STOCK_PAGE_SIZE, totalItems);
  const paginatedItems = useMemo(
    () => items.slice(startIndex, endIndex),
    [endIndex, items, startIndex]
  );
  const pagination = {
    currentPage: normalizedPage,
    endItem: endIndex,
    isNextDisabled: normalizedPage >= totalPages,
    isPreviousDisabled: normalizedPage <= 1,
    onNext: () => setCurrentPage((page) => Math.min(page + 1, totalPages)),
    onPrevious: () => setCurrentPage((page) => Math.max(page - 1, 1)),
    startItem: totalItems ? startIndex + 1 : 0,
    totalItems,
    totalPages,
  };

  return (
    <article className={`${surfaceClassName} overflow-hidden`}>
      <div className="p-5 sm:p-6">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="m-0 text-[0.82rem] text-[var(--theme-text-muted)] sm:text-sm">
              Seaweeds Stocks by Supplier
            </p>
            <strong className="mt-1 block text-[1.35rem] font-semibold text-[var(--theme-text)]">
              KG Net breakdown
            </strong>
          </div>
          <span className="rounded-full border border-[var(--theme-border)] bg-[var(--theme-surface-soft)] px-3 py-1 text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-subtle)]">
            Completed P.O.
          </span>
        </div>

        {items.length ? (
          <div className="grid gap-2">
            {paginatedItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between gap-4 rounded-[16px] border border-[var(--theme-border-soft)] bg-[color-mix(in_srgb,var(--theme-surface-raised)_92%,white)] px-4 py-3 shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.04)]"
              >
                <span className="grid min-w-0 gap-0.5">
                  <span className="truncate text-[0.94rem] font-medium text-[var(--theme-text-strong)]">
                    {item.supplierName}
                  </span>
                  {item.sacksMarks?.length ? (
                    <span className="truncate text-[0.78rem] font-medium text-[var(--theme-text-muted)]">
                      Sacks Mark: {item.sacksMarks.join(', ')}
                    </span>
                  ) : null}
                </span>
                <strong className="shrink-0 text-[1rem] font-semibold text-[var(--theme-text)]">
                  {formatWholeNumber(item.quantityKg)} kg
                </strong>
              </div>
            ))}
          </div>
        ) : (
          <DashboardEmptyState compact message="No completed supplier stock yet." />
        )}
      </div>

      {totalItems > SUPPLIER_STOCK_PAGE_SIZE ? (
        <PaginationFooter {...pagination} />
      ) : null}
    </article>
  );
}

function formatWholeNumber(value) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

export default StaffDashboardPage;
